import React, { useEffect, useState } from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {Card, Descriptions, Typography, Spin, Tag, Button, Image, Col, Row, Empty} from "antd";
import axios from "axios";
import PageTitle from "../../components/PageTitle";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBackward} from "@fortawesome/free-solid-svg-icons";
import "../../css/constants.css"
import "../../css/imagePreviewCustomZindex.css"
import {useWindowDimensions} from "../../contexts/WindowDimensionsContext";

const TruckDetails = ({ backendApiUrl }) => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const plate = searchParams.get("plate");
    const navigate = useNavigate();
    const windowDimensions = useWindowDimensions();

    const [truck, setTruck] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (plate) {
            axios.get(`${backendApiUrl}/trucks/${plate}`)
                .then(response => {
                    setTruck(response.data);
                    setLoading(false);
                })
                .catch(error => {
                    console.error("Erreur lors de la récupération des détails du camion", error);
                    setLoading(false);
                });
        }
    }, [plate, backendApiUrl]);

    if (loading) {
        return <Spin size="large" style={{ display: 'block', margin: '50px auto' }} />;
    }

    if (!truck) {
        return <p>Truck not found</p>;
    }

    const formatDate = (date) => date ? new Date(date).toLocaleDateString("fr-FR") : "N/A";

    const getStatusTagColor = (status) => {
        if (status >= 7) return 'green';   // Good (7-10)
        if (status >= 5) return 'orange';  // Medium (5-6)
        if (status >= 3) return 'red';     // Bad (3-4)
        return 'purple';                   // Horrible (0-2)
    };

    const getAvailabilityTagColor = (status) => {
        switch (status) {
            case "Disponible":
                return 'green';   // Available
            case "Maintenance programée":
                return 'orange';  // Partially Available
            case "Contre-visite":
                return 'magenta';  // Counter-visit
            case "En maintenance":
                return 'red';     // Not Available
            default:
                return 'purple'; // Unavailable for a long time
        }
    };

    const getTagColor = (date, type, months, isRed = false) => {
        const newDate = new Date(date);
        newDate.setMonth(newDate.getMonth() + months);

        const today = new Date();
        const diffInMs = new Date(newDate) - today;
        const daysDifference = diffInMs / (1000 * 3600 * 24);
        let color = 'green';
        if (isRed)
            return "magenta";
        switch (type) {
            case 'inspection':
            case 'technicalReview':
            case 'speedLimiter':
            case 'tachograph':
            case 'extinguisher':
            case 'tailgates':
                if (daysDifference < 0) {
                    if (daysDifference <= -30) {
                        color = 'purple'; // Dépassé depuis plus d'un mois
                    } else {
                        color = 'red'; // Dépassé mais moins d'un mois
                    }
                } else if (daysDifference <= 30) {
                    color = 'orange'; // Proche de la date d'échéance
                }
                break;
            default:
                color = 'green'; // Si rien n'est précisé
        }

        return color;
    };

    const renderFileLinks = (files) => {
        if (!files || files.length === 0) return "N/A";

        return files.map((file, index) => {
            let url = "";
            let name = "Document";

            if (typeof file === "string") {
                url = file;
                name = extractFileNameFromUrl(file);
            } else if (typeof file === "object") {
                url = file.url || file.firebaseUrl || "";
                name = file.name || extractFileNameFromUrl(url);
            }

            if (!url) return null;

            return (
                <span
                    key={index}
                    onClick={() => window.open(url, "_blank")}
                    className="block text-blue-600 hover:underline cursor-pointer"
                    title={name}
                >
                📄 {formatFileName(name.replace("trucks/", ""))}
            </span>
            );
        });
    };

    const extractFileNameFromUrl = (url) => {
        try {
            const decoded = decodeURIComponent(url);
            const match = decoded.match(/\/o\/[^/]*\/[^/]*\/([^?]+)/);
            if (match && match[1]) return match[1];
            const parts = decoded.split("/").pop().split("?")[0];
            return parts || "Document";
        } catch {
            return "Document";
        }
    };

    const formatFileName = (name) => {
        if (!name) return "";
        return name.length > 26 ? `${name.slice(0, 20)}...${name.slice(-6)}` : name;
    };

    const renderImages = (pictures) => {
        if (!pictures || pictures.length === 0) {
            return <Empty description="Aucune image disponible" />;
        }

        return (
            <Image.PreviewGroup>
                <Row gutter={[12, 12]}>
                    {pictures.map((picture, index) => (
                        <Col span={windowDimensions.wWCheck(1600) ? 2 : windowDimensions.wWCheck(1200) ? 4 : windowDimensions.wWCheck(800) ? 6 : windowDimensions.wWCheck(600) ? 10 : 14 } key={index}>
                            <Card
                                hoverable
                                bodyStyle={{ padding: 4 }}
                                style={{ borderRadius: 8 }}
                            >
                                <Image
                                    src={picture}
                                    alt={`Image ${index + 1}`}
                                    style={{
                                        width: '100%',
                                        height: 100,
                                        objectFit: 'cover',
                                        borderRadius: 6,
                                    }}
                                />
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Image.PreviewGroup>
        );
    };

    const commonDescriptionProps = {
        bordered: true,
        column: { xs: 1, sm: 1, md: 1, lg: 2, xl: 2, xxl: 3 },
        tableLayout: "fixed",
        labelStyle: { width: 205, wordBreak: "break-word" },
        style: { wordBreak: "break-word" },
    };

    return (
        <div className={"mb-auto"}>
            <div style={{ marginBottom: "30px", width: "100%" }}>
                <div className={"w-100 flex justify-between gap-2 flex-wrap"}>
                    <div className={"flex gap-5 flex-wrap flex-center"}>
                        <PageTitle title={"Détails du camion " + truck.licensePlate}></PageTitle>
                        <Button type={"primary"} icon={<FontAwesomeIcon icon={faBackward}/>} onClick={() => navigate("/trucks")}>
                            Retour à la liste
                        </Button>
                    </div>
                </div>
            </div>

            {/* Informations Générales */}
            <Card title="Informations générales">
                <Descriptions {...commonDescriptionProps}>
                    <Descriptions.Item label="Libellé">{truck.label || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Kilométrage">{truck.miles} km</Descriptions.Item>
                    <Descriptions.Item label="Entreprise locataire">{truck.rentalCompany || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Marque">{truck.brand || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Licence">{truck.license || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Contrat de location">{renderFileLinks(truck.rentalSheet)}</Descriptions.Item>
                    <Descriptions.Item label="Plaque">{truck.licensePlate}</Descriptions.Item>
                    <Descriptions.Item label="Propriétaire">{truck.owner}</Descriptions.Item>
                    <Descriptions.Item label="Constat">{truck.report ? "Oui" : "Non"}</Descriptions.Item>
                </Descriptions>
            </Card>

            {/* Contrôles */}
            <Card title="Contrôles" style={{ marginTop: 20 }}>
                <Descriptions {...commonDescriptionProps}>
                    <Descriptions.Item label="Inspection">
                        <Tag color={getTagColor(truck.inspection?.lastPerformed, 'inspection', 12)}>
                            {formatDate(truck.inspection?.lastPerformed)} {"->"} {truck.inspection?.performedBy}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Révision Technique">
                        <Tag color={getTagColor(truck.technicalReview?.lastPerformed, 'technicalReview', 12)}>
                            {formatDate(truck.technicalReview?.lastPerformed)} {"->"} {formatDate(truck.technicalReview?.nextScheduled)}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Limiteur de vitesse">
                        <Tag color={getTagColor(truck.speedLimiter?.lastPerformed, 'speedLimiter', 12)}>
                            {formatDate(truck.speedLimiter?.lastPerformed)} {"->"} {formatDate(truck.speedLimiter?.nextScheduled)}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Tachygraphe">
                        <Tag color={getTagColor(truck.tachograph?.lastPerformed, 'tachograph', 24)}>
                            {formatDate(truck.tachograph?.lastPerformed)} {"->"} {formatDate(truck.tachograph?.nextScheduled)}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Extincteur">
                        <Tag color={getTagColor(truck.extinguisher?.expiryDate, 'extinguisher', 12, truck.extinguisher?.needsReplacement)}>
                            {formatDate(truck.extinguisher?.expiryDate)} {"->"} {truck.extinguisher?.needsReplacement ? "À remplacer" : "En bon état"}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Hayon">
                        {truck.tailgates?.lastPerformed ? (
                            <Tag color={getTagColor(truck.tailgates?.lastPerformed, 'tailgates', 12)}>
                                {formatDate(truck.tailgates?.lastPerformed)} {"->"} {formatDate(truck.tailgates?.nextScheduled)}
                            </Tag>
                        ) : "N/A"}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Card title="Contrôles Spécifiques" style={{ marginTop: 20 }}>
                <Descriptions {...commonDescriptionProps}>
                    <Descriptions.Item label="Angles morts OK">{truck.anglesMortsOK ? "Oui" : "Non"}</Descriptions.Item>
                    <Descriptions.Item label="Logo Team Kimmel">{truck.logoTeamKimmel ? "Oui" : "Non"}</Descriptions.Item>
                    <Descriptions.Item label="Logos OK">
                        {truck.logoTeamKimmel ? (truck.logosOK ? "Oui" : "Non") : "N/A"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Transpal">{truck.transpal ? "Oui" : "Non"}</Descriptions.Item>
                    <Descriptions.Item label="Type de Transpal">
                        {truck.transpal ? (truck.transpalType ? "Manuel" : "Électrique") : "N/A"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Hayon">{truck.hasTailgates ? "Oui" : "Non"}</Descriptions.Item>
                </Descriptions>
            </Card>

            {/* Statut */}
            <Card title="Statut" style={{ marginTop: 20 }}>
                <Descriptions
                    bordered
                    column={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 2, xxl: 2 }}
                    size="middle"
                    labelStyle={{ width: 205 }}
                >
                    <Descriptions.Item label="État intérieur">
                        <Tag color={getStatusTagColor(truck.insideStatus)}>
                            {truck.insideStatus} / 10
                        </Tag>
                    </Descriptions.Item>

                    <Descriptions.Item label="État extérieur">
                        <Tag color={getStatusTagColor(truck.outsideStatus)}>
                            {truck.outsideStatus} / 10
                        </Tag>
                    </Descriptions.Item>

                    <Descriptions.Item label="Photos de l'intérieur" span={2}>
                        {renderImages(truck.insideStatusPictures)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Photos de l'extérieur" span={2}>
                        {renderImages(truck.outsideStatusPictures)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Disponibilité">
                        <Tag color={getAvailabilityTagColor(truck.availability.status)}>
                            {truck.availability.status}
                        </Tag>
                    </Descriptions.Item>

                    <Descriptions.Item label="Description disponibilité">
                        {truck.availability.description || "N/A"}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {/* Commentaires */}
            <Card title="Commentaires" style={{ marginTop: 20, marginBottom: 20 }}>
                <Descriptions bordered column={1}>
                    {truck.comments?.map((comment, index) => (
                        <Descriptions.Item key={index} label={`Commentaire ${index + 1}`} labelStyle={{ width: 205 }}>
                            <strong>{formatDate(comment.date)} - {comment.performedBy} :</strong> {comment.description}
                        </Descriptions.Item>
                    ))}
                </Descriptions>
            </Card>
        </div>
    );
};

export default TruckDetails;